import React from 'react';
import { Box, Typography, alpha, Chip, CircularProgress } from '@mui/material';
import { palette } from '../../theme';

/**
 * Primary translucent surface used across the console.
 * Adds depth through layered gradients, a luminous hairline border and a
 * soft volumetric shadow instead of heavy borders or flat fills.
 */
export function GlassPanel({
  children,
  elevation = 0,
  glow = 0,
  sx = {},
  ...rest
}) {
  return (
    <Box
      {...rest}
      sx={{
        position: 'relative',
        borderRadius: 4,
        border: `1px solid ${alpha(palette.primary, 0.13 + glow * 0.08)}`,
        background: `linear-gradient(158deg, ${alpha('#0E1730', 0.72)}, ${alpha('#070C1C', 0.78)})`,
        backdropFilter: 'blur(22px) saturate(135%)',
        WebkitBackdropFilter: 'blur(22px) saturate(135%)',
        boxShadow: `0 22px 54px -26px rgba(2,6,23,0.95), inset 0 1px 0 ${alpha('#FFFFFF', 0.045)}`,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `radial-gradient(120% 90% at 8% -10%, ${alpha(palette.primary, 0.12)}, transparent 58%)`,
          opacity: 0.9,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          // NOTE: must stay a string - the sx sizing transform converts a
          // numeric value <= 1 into a percentage, which would stretch this
          // hairline over the whole panel and swallow every click.
          height: '1px',
          pointerEvents: 'none',
          background: `linear-gradient(90deg, transparent, ${alpha(palette.primary, 0.45)}, transparent)`,
          opacity: 0.7,
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Section heading used at the top of every panel. */
export function SectionTitle({ title, subtitle, icon, action, accent = palette.primary }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        {icon && (
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 34,
              height: 34,
              borderRadius: 2.25,
              color: accent,
              background: alpha(accent, 0.12),
              border: `1px solid ${alpha(accent, 0.28)}`,
              boxShadow: `inset 0 0 18px ${alpha(accent, 0.16)}`,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '0.95rem',
              fontWeight: 700,
              letterSpacing: '0.02em',
              color: 'text.primary',
              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontSize: '0.74rem',
                color: 'text.secondary',
                mt: 0.25,
                lineHeight: 1.35,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {action && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>{action}</Box>
      )}
    </Box>
  );
}

const TONE_COLORS = {
  success: palette.success,
  danger: palette.danger,
  warning: palette.warning,
  info: palette.primary,
  secondary: palette.secondary,
  neutral: palette.secondaryText,
};

/** Compact status indicator with a breathing indicator dot. */
export function StatusPill({ label, tone = 'neutral', pulse = true, size = 'small' }) {
  const color = TONE_COLORS[tone] || TONE_COLORS.neutral;
  return (
    <Chip
      size="small"
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85 }}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: color,
              boxShadow: `0 0 8px ${color}`,
              animation: pulse ? 'holoBlink 2.4s ease-in-out infinite' : 'none',
            }}
          />
          <Box component="span" sx={{ fontSize: size === 'small' ? '0.7rem' : '0.78rem', fontWeight: 700, letterSpacing: '0.08em' }}>
            {String(label).toUpperCase()}
          </Box>
        </Box>
      }
      sx={{
        height: size === 'small' ? 24 : 30,
        color,
        background: alpha(color, 0.1),
        border: `1px solid ${alpha(color, 0.28)}`,
        backdropFilter: 'blur(10px)',
        '& .MuiChip-label': { px: 1.15 },
      }}
    />
  );
}

/** Friendly empty state used by every data surface. */
export function EmptyState({ icon, title, description, action, minHeight = 200 }) {
  return (
    <Box
      sx={{
        minHeight,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        textAlign: 'center',
        px: 3,
        py: 4,
      }}
    >
      {icon && (
        <Box
          sx={{
            width: 52,
            height: 52,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 3,
            color: alpha(palette.primary, 0.85),
            background: alpha(palette.primary, 0.08),
            border: `1px solid ${alpha(palette.primary, 0.18)}`,
            mb: 0.5,
          }}
        >
          {icon}
        </Box>
      )}
      <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>{title}</Typography>
      {description && (
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem', maxWidth: 380, lineHeight: 1.5 }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 1.5 }}>{action}</Box>}
    </Box>
  );
}

/** Panel level loading state. */
export function PanelLoader({ label = 'Synchronising telemetry…' }) {
  return (
    <Box sx={{ minHeight: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
      <CircularProgress size={26} thickness={3} />
      <Typography sx={{ color: 'text.secondary', fontSize: '0.78rem', letterSpacing: '0.08em' }}>
        {label.toUpperCase()}
      </Typography>
    </Box>
  );
}

/** Thin luminous divider used between stacked blocks. */
export function HoloDivider({ sx = {} }) {
  return (
    <Box
      sx={{
        height: '1px',
        background: `linear-gradient(90deg, transparent, ${alpha(palette.primary, 0.28)}, transparent)`,
        ...sx,
      }}
    />
  );
}
